/*
  Behaviour for `snippets/oneat-product-card.liquid`.

  Everything is delegated from `document`, and the money formatter and cart add
  are looked up per event rather than bound per card. That is deliberate:
  `oneat-recently-viewed` injects its cards after load, and the recommendations
  band swaps its skeletons for real cards, so anything bound at parse time
  would miss most of the cards on the page.

  Loaded with `defer` from each section; the browser dedupes the URL, so two
  bands on one page still run this once. The `dataset` guard makes a second
  execution harmless anyway.
*/
(function () {
  if (document.documentElement.dataset.opcCardBound === '1') return;
  document.documentElement.dataset.opcCardBound = '1';

  var CART_ADD_URL = document.currentScript && document.currentScript.dataset.cartAddUrl;
  var FLY_TO_CART_URL = document.currentScript && document.currentScript.dataset.flyToCartUrl;

  /**
   * Mirrors the theme's `money` filter output for INR, which is what the
   * Liquid side renders. Only used when a variant change rewrites a price.
   * @param {number} cents
   * @returns {string}
   */
  function money(cents) {
    if (cents == null) return '';
    var parts = (cents / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '₹ ' + parts.join('.');
  }

  /**
   * @param {Element} card
   * @returns {Record<string, {price: number, compareAt: number, available: boolean}>}
   */
  function variantsOf(card) {
    if (card.__opcVariants) return card.__opcVariants;
    var island = card.querySelector('[data-opc-variants]');
    try {
      card.__opcVariants = island ? JSON.parse(island.textContent) : {};
    } catch (error) {
      card.__opcVariants = {};
    }
    return card.__opcVariants;
  }

  /* ── Variant change ───────────────────────────────────────────────────── */
  document.addEventListener('change', function (event) {
    var select = event.target.closest ? event.target.closest('[data-opc-variant]') : null;
    if (!select) return;

    var card = select.closest('[data-opc-card]');
    if (!card) return;

    var variant = variantsOf(card)[select.value];
    if (!variant) return;

    var price = card.querySelector('[data-opc-price]');
    if (price) price.textContent = money(variant.price);

    var discounted = variant.compareAt > variant.price;

    var compare = card.querySelector('[data-opc-compare]');
    if (compare) {
      compare.textContent = discounted ? money(variant.compareAt) : '';
      compare.toggleAttribute('hidden', !discounted);
    }

    /* The percentage lives inside the badge, so the badge as a whole is
       toggled - hiding just the number would leave a bare "Off". */
    var off = card.querySelector('[data-opc-off]');
    var pct = card.querySelector('[data-opc-discount]');
    if (pct && discounted) {
      pct.textContent = Math.round(((variant.compareAt - variant.price) / variant.compareAt) * 100) + '%';
    }
    if (off) off.toggleAttribute('hidden', !discounted);

    var button = card.querySelector('[data-opc-atc]');
    if (button) {
      button.dataset.variantId = select.value;
      button.disabled = !variant.available;
      var label = button.querySelector('.opc-atc-label');
      if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
  });

  /* ── Add to cart ──────────────────────────────────────────────────────── */

  /**
   * Mirrors #animateFlyToCart in assets/product-form.js: build a
   * <fly-to-cart>, point it from the card image at the header cart icon and
   * let assets/fly-to-cart.js animate it.
   * @param {Element} card
   * @param {Element} button
   */
  function flyToCart(card, button) {
    try {
      var image = card.querySelector('img');
      var cartIcon = document.querySelector('.header-actions__cart-icon');
      if (!image || !cartIcon || !FLY_TO_CART_URL) return;

      var paint = function () {
        var element = document.createElement('fly-to-cart');
        element.classList.add('fly-to-cart--quick');
        element.style.setProperty('background-image', 'url(' + (image.currentSrc || image.src) + ')');
        element.style.setProperty('--start-opacity', '0');
        element.source = button;
        element.destination = cartIcon;
        document.body.appendChild(element);
      };

      if (customElements.get('fly-to-cart')) paint();
      else import(FLY_TO_CART_URL).then(paint).catch(function () {});
    } catch (error) {
      /* The animation is decorative - never let it block the add. */
    }
  }

  /**
   * Horizon 4.1.4 owns the dialog on `<theme-drawer id="cart-drawer">`, not on
   * `cart-drawer-component` - that element no longer has an open(). It opens
   * itself when it sees the cart event and carries `auto-open`, which is how
   * the theme's own buttons do it, so this is only the fallback for the drawer
   * not having picked the event up.
   */
  function openCartDrawer() {
    var drawer = document.querySelector('theme-drawer#cart-drawer');
    var cart = drawer && drawer.querySelector('cart-drawer-component');
    if (!drawer || !cart || !cart.hasAttribute('auto-open')) return;
    setTimeout(function () {
      if (drawer.isOpen) return;
      if (typeof drawer.open === 'function') drawer.open();
    }, 250);
  }

  document.addEventListener('click', async function (event) {
    var button = event.target.closest ? event.target.closest('[data-opc-atc]') : null;
    if (!button) return;
    if (button.disabled || button.classList.contains('is-loading')) return;

    var card = button.closest('[data-opc-card]');
    if (!card || !CART_ADD_URL) return;

    button.classList.add('is-loading');
    button.disabled = true;

    /* Fired on click, not after the add resolves, so the badge flies during
       the /cart/add.js round trip and the drawer opens as it lands - the same
       timing assets/product-form.js uses. */
    flyToCart(card, button);

    try {
      var module = await import(CART_ADD_URL);
      await module.oneatAddToCart({
        variantId: button.dataset.variantId,
        quantity: 1,
        source: 'oneat-product-card',
        productId: card.dataset.productId,
        dispatchOn: button,
      });

      openCartDrawer();
      button.classList.remove('is-loading');
      button.setAttribute('data-added', 'true');
      setTimeout(function () {
        button.removeAttribute('data-added');
        button.disabled = false;
      }, 2400);
    } catch (error) {
      console.error('[oneat-product-card] add-to-cart:', error);
      button.classList.remove('is-loading');
      var label = button.querySelector('.opc-atc-label');
      if (label) {
        var original = label.textContent;
        label.textContent = 'Error – try again';
        setTimeout(function () {
          label.textContent = original;
          button.disabled = false;
        }, 2500);
      } else {
        button.disabled = false;
      }
    }
  });
})();
